import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { NoticeService } from '@/services/notice.service'
import { useLocaleStore } from '@/stores/locale.store'
import { lsGet, lsSet } from '@/utils/storage'
import type { Locale } from '@/router/locales'
import type { NoticeModel } from '@/models/notice'

type CacheMeta = {
  fetchedAt: number
  attemptedAt: number
  ttlMs: number
  locale: Locale | null
}

const now = () => Date.now()
const DISMISSED_NOTICE_IDS_STORAGE_KEY = 'awww:dismissed-notice-ids:v1'
const FAILED_REQUEST_RETRY_INTERVAL_MS = 30_000
const NOTICE_REQUESTS_ENABLED = false

function loadDismissedNoticeIds(): Set<string> {
  const raw = lsGet(DISMISSED_NOTICE_IDS_STORAGE_KEY)
  if (!raw) return new Set()

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()

    return new Set(
      parsed.filter((item): item is string => typeof item === 'string' && item.length > 0),
    )
  } catch {
    return new Set()
  }
}

function persistDismissedNoticeIds(ids: Set<string>) {
  lsSet(DISMISSED_NOTICE_IDS_STORAGE_KEY, JSON.stringify([...ids]))
}

export const useNoticeStore = defineStore('notice', () => {
  const localeStore = useLocaleStore()
  const notices = ref<NoticeModel[]>([])
  const dismissed = ref<Set<string>>(loadDismissedNoticeIds())
  const loading = ref(false)
  const error = ref<unknown>(null)
  const cache = ref<CacheMeta>({
    fetchedAt: 0,
    attemptedAt: 0,
    ttlMs: 5 * 60_000,
    locale: null,
  })
  let inFlight: Promise<void> | null = null

  const visibleNotices = computed(() => {
    const timestamp = now()

    return notices.value
      .filter((item) => !dismissed.value.has(item.id))
      .filter((item) => {
        const startsAt = item.startsAt ? Date.parse(item.startsAt) : Number.NaN
        const endsAt = item.endsAt ? Date.parse(item.endsAt) : Number.NaN

        if (Number.isFinite(startsAt) && timestamp < startsAt) return false
        if (Number.isFinite(endsAt) && timestamp > endsAt) return false

        return true
      })
  })

  function isFresh() {
    return (
      cache.value.fetchedAt > 0
      && cache.value.locale === localeStore.currentLocale
      && now() - cache.value.fetchedAt < cache.value.ttlMs
    )
  }

  function isCoolingDownAfterFailure() {
    return (
      error.value !== null
      && cache.value.attemptedAt > 0
      && cache.value.locale === localeStore.currentLocale
      && now() - cache.value.attemptedAt < FAILED_REQUEST_RETRY_INTERVAL_MS
    )
  }

  function ensure(opts?: { force?: boolean }): Promise<void> {
    if (!NOTICE_REQUESTS_ENABLED) return Promise.resolve()

    if (!opts?.force && (isFresh() || isCoolingDownAfterFailure())) {
      return Promise.resolve()
    }

    if (inFlight) return inFlight

    loading.value = true
    error.value = null

    const locale = localeStore.currentLocale
    if (cache.value.locale !== locale) {
      cache.value.fetchedAt = 0
    }
    cache.value.attemptedAt = now()
    cache.value.locale = locale

    inFlight = (async () => {
      try {
        notices.value = await NoticeService.notices()
        cache.value.fetchedAt = now()
      } catch (e) {
        error.value = e
      } finally {
        loading.value = false
        inFlight = null
      }
    })()

    return inFlight
  }

  function dismiss(id: string) {
    const next = new Set([...dismissed.value, id])

    dismissed.value = next
    persistDismissedNoticeIds(next)
  }

  function invalidate() {
    cache.value.fetchedAt = 0
    cache.value.attemptedAt = 0
    cache.value.locale = null
  }

  return {
    notices,
    visibleNotices,
    loading,
    error,
    ensure,
    dismiss,
    invalidate,
  }
})
